/**
 * tournaments-directory.ts - Public tournaments directory UI
 */

import { searchPublicTournaments, type PublicTournamentFilters } from '../api/public-tournaments-api';

// Leaflet global declaration
declare const L: any;

// Default tournament logo
const DEFAULT_TOURNAMENT_LOGO = '/images/tournament-logo.png';

// State
let currentViewMode: 'cards' | 'table' = 'cards';
let currentSortOrder: string = 'startDate-desc'; // Default: newest first
let directoryMap: any = null;
let userLocationMarker: any = null;
let radiusCircle: any = null;
let tournamentMarkers: any[] = [];
let userCoords: { lat: number; lon: number } | null = null;
let currentTournaments: any[] = [];

/**
 * Initialize the tournaments directory page
 */
export async function initTournamentsDirectory(): Promise<void> {
  // Check if we're on the tournaments page
  if (!document.getElementById('tournaments-directory')) {
    return;
  }

  // Check for registration success indicator
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('registered') === '1') {
    // Show brief registration success banner
    showRegistrationBanner();
    // Clean up URL
    window.history.replaceState({}, '', window.location.pathname);
  }

  // Set up search and filter handlers
  setupSearchHandlers();

  // Set up location autocomplete
  setupLocationAutocomplete();

  // Initialize map
  initDirectoryMap();

  // Set up view toggle and filter toggle
  setupViewToggle();
  setupFilterToggle();

  // Listen for language changes to re-render translations
  setupLanguageChangeListener();

  // Load initial tournaments
  await searchTournaments();

  // Ask for user location after 5 seconds
  setTimeout(() => {
    if (!userCoords) {
      showLocationPrompt();
    }
  }, 5000);
}

/**
 * Set up listener for language changes to re-render tournament cards/table
 */
function setupLanguageChangeListener(): void {
  window.addEventListener('languageChanged', () => {
    // Re-render current tournaments with new language
    if (currentTournaments.length > 0) {
      if (currentViewMode === 'cards') {
        displayTournaments(currentTournaments);
      } else {
        displayTournamentsTable(currentTournaments);
      }
    }

    // Update the count text
    const countEl = document.getElementById('tournaments-count');
    if (countEl && currentTournaments.length >= 0) {
      countEl.textContent = window.i18n.t('tournaments.card.tournamentsFound', { count: currentTournaments.length });
    }
  });
}

/**
 * Initialize the directory map
 */
function initDirectoryMap(): void {
  const mapContainer = document.getElementById('directory-map');
  if (!mapContainer || typeof L === 'undefined') return;

  // Initialize map with default world view centered on Europe
  directoryMap = L.map('directory-map', {
    center: [48, 10], // Europe center
    zoom: 4,
    minZoom: 2,
    maxZoom: 18,
    zoomControl: true,
    scrollWheelZoom: true
  });

  // Add OpenStreetMap tiles
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(directoryMap);

  // Update radius circle when radius changes
  const radiusSelect = document.getElementById('radius-select') as HTMLSelectElement;
  if (radiusSelect) {
    radiusSelect.addEventListener('change', () => {
      // Update for user location
      if (userCoords) {
        updateRadiusCircle();
      }
      // Or for searched location
      else if (searchedLocationCoords) {
        updateMapToSearchedLocation();
      }
    });
  }
}

/**
 * Show location permission prompt
 */
function showLocationPrompt(): void {
  const prompt = document.getElementById('map-location-prompt');
  if (prompt) {
    prompt.style.display = 'block';
  }
}

/**
 * Hide location permission prompt
 */
function hideLocationPrompt(): void {
  const prompt = document.getElementById('map-location-prompt');
  if (prompt) {
    prompt.style.display = 'none';
  }
}

/**
 * Request user's location
 */
function requestUserLocation(): void {
  if (!navigator.geolocation) {
    console.warn('Geolocation not supported');
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      userCoords = {
        lat: position.coords.latitude,
        lon: position.coords.longitude
      };
      hideLocationPrompt();
      updateMapToUserLocation();

      // Reverse geocode to get location name
      reverseGeocode(userCoords.lat, userCoords.lon);
    },
    (error) => {
      console.warn('Geolocation error:', error.message);
      hideLocationPrompt();
    },
    {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300000 // 5 minutes cache
    }
  );
}

/**
 * Reverse geocode coordinates to get location name
 */
async function reverseGeocode(lat: number, lon: number): Promise<void> {
  try {
    const response = await fetch(`/api/tournaments/reverse-geocode?lat=${lat}&lon=${lon}`);
    if (response.ok) {
      const data = await response.json();
      if (data.displayName) {
        const locationInput = document.getElementById('location-search') as HTMLInputElement;
        if (locationInput && !locationInput.value) {
          locationInput.value = data.displayName;
          // Trigger search with the new location
          searchTournaments();
        }
      }
    }
  } catch (error) {
    console.error('Reverse geocode error:', error);
  }
}

/**
 * Update map to center on user location
 */
function updateMapToUserLocation(): void {
  if (!directoryMap || !userCoords) return;

  // Center map on user location
  directoryMap.setView([userCoords.lat, userCoords.lon], 8);

  // Add or update user location marker
  if (userLocationMarker) {
    userLocationMarker.setLatLng([userCoords.lat, userCoords.lon]);
  } else {
    const userIcon = L.divIcon({
      className: 'user-location-marker-icon',
      html: '<div class="user-location-marker"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7]
    });
    userLocationMarker = L.marker([userCoords.lat, userCoords.lon], { icon: userIcon })
      .addTo(directoryMap);
  }

  // Update radius circle
  updateRadiusCircle();
}

/**
 * Update the radius circle on the map
 */
function updateRadiusCircle(): void {
  if (!directoryMap || !userCoords) return;

  const radiusSelect = document.getElementById('radius-select') as HTMLSelectElement;
  const radiusKm = radiusSelect ? parseInt(radiusSelect.value) : 50;
  const radiusMeters = radiusKm * 1000;

  // Remove existing circle
  if (radiusCircle) {
    directoryMap.removeLayer(radiusCircle);
  }

  // Add new circle
  radiusCircle = L.circle([userCoords.lat, userCoords.lon], {
    radius: radiusMeters,
    className: 'radius-circle',
    fillColor: '#16a085',
    fillOpacity: 0.1,
    color: '#16a085',
    weight: 2,
    opacity: 0.4
  }).addTo(directoryMap);

  // Fit map to show the circle
  directoryMap.fitBounds(radiusCircle.getBounds(), { padding: [20, 20] });
}

/**
 * Get tournament status based on dates
 */
function getTournamentStatus(tournament: any): 'upcoming' | 'ongoing' | 'completed' {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const startDate = new Date(tournament.startDate);
  const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

  const endDate = tournament.endDate ? new Date(tournament.endDate) : new Date(tournament.startDate);
  const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  if (today < startDay) return 'upcoming';
  if (today > endDay) return 'completed';
  return 'ongoing';
}

/**
 * Update tournament markers on map
 */
function updateTournamentMarkers(tournaments: any[]): void {
  if (!directoryMap) return;

  // Clear existing markers
  tournamentMarkers.forEach(marker => directoryMap.removeLayer(marker));
  tournamentMarkers = [];

  // Group tournaments by location (same coordinates)
  const locationGroups = new Map<string, any[]>();
  tournaments.forEach(tournament => {
    if (tournament.latitude && tournament.longitude) {
      const key = `${tournament.latitude},${tournament.longitude}`;
      if (!locationGroups.has(key)) {
        locationGroups.set(key, []);
      }
      locationGroups.get(key)!.push(tournament);
    }
  });

  // Add markers for each location group
  locationGroups.forEach((tournamentsAtLocation, _key) => {
    const firstTournament = tournamentsAtLocation[0];

    // Determine marker color based on whether any tournament is upcoming
    const hasUpcoming = tournamentsAtLocation.some(t => getTournamentStatus(t) === 'upcoming');
    const markerClass = hasUpcoming ? 'directory-tournament-marker' : 'directory-tournament-marker ongoing';

    const icon = L.divIcon({
      className: 'directory-tournament-marker-icon',
      html: `<div class="${markerClass}"></div>`,
      iconSize: [10, 10],
      iconAnchor: [5, 5]
    });

    // Build popup content for all tournaments at this location
    const popupContent = tournamentsAtLocation.map(tournament => {
      const tournamentUrl = tournament.shareToken ? `/share/${tournament.shareToken}` : `/?tournamentId=${tournament.id}`;
      return `
        <div class="map-tournament-popup">
          <a href="${tournamentUrl}" class="popup-tournament-link">
            <strong>${escapeHtml(tournament.name)}</strong>
          </a>
          <div class="popup-tournament-date">${formatDate(tournament.startDate)}</div>
        </div>
      `;
    }).join('<hr style="margin: 8px 0; border: none; border-top: 1px solid #ddd;">');

    // Add location info once at the bottom if available
    const locationInfo = firstTournament.location
      ? `<div class="popup-tournament-location" style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #ddd;">${escapeHtml(simplifyVenueName(firstTournament.location))}</div>`
      : '';

    const marker = L.marker([firstTournament.latitude, firstTournament.longitude], { icon: icon })
      .bindPopup(popupContent + locationInfo, { maxHeight: 200 })
      .addTo(directoryMap);

    // Make marker clickable - open popup on click
    marker.on('click', () => {
      marker.openPopup();
    });

    tournamentMarkers.push(marker);
  });
}

/**
 * Setup view toggle functionality
 */
function setupViewToggle(): void {
  // Expose to window for onclick handlers
  (window as any).setViewMode = setViewMode;
}

/**
 * Set the view mode (cards or table)
 */
function setViewMode(mode: 'cards' | 'table'): void {
  currentViewMode = mode;

  const cardsBtn = document.getElementById('view-cards-btn');
  const tableBtn = document.getElementById('view-table-btn');
  const cardsList = document.getElementById('tournaments-list');
  const tableContainer = document.getElementById('tournaments-table');

  if (cardsBtn && tableBtn) {
    cardsBtn.classList.toggle('active', mode === 'cards');
    tableBtn.classList.toggle('active', mode === 'table');
  }

  if (cardsList && tableContainer) {
    cardsList.style.display = mode === 'cards' ? '' : 'none';
    tableContainer.style.display = mode === 'table' ? '' : 'none';
  }

  // Re-render with current tournaments
  if (currentTournaments.length > 0) {
    if (mode === 'cards') {
      displayTournaments(currentTournaments);
    } else {
      displayTournamentsTable(currentTournaments);
    }
  }
}

/**
 * Setup filter toggle functionality
 */
function setupFilterToggle(): void {
  // Expose to window for onclick handlers
  (window as any).toggleFilters = toggleFilters;
  (window as any).requestUserLocation = requestUserLocation;

  // Update filter badge on filter changes
  const filterElements = ['status-filter', 'time-control-filter', 'format-filter', 'date-from-filter', 'date-to-filter', 'rated-only-filter'];
  filterElements.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('change', updateFilterBadge);
    }
  });
}

/**
 * Toggle the filters panel
 */
function toggleFilters(): void {
  const panel = document.getElementById('filters-panel');
  const btn = document.getElementById('toggle-filters-btn');

  if (panel && btn) {
    const isVisible = panel.style.display !== 'none';
    panel.style.display = isVisible ? 'none' : 'block';
    btn.classList.toggle('active', !isVisible);
  }
}

/**
 * Update the filter badge count
 */
function updateFilterBadge(): void {
  const badge = document.getElementById('filter-badge');
  if (!badge) return;

  let count = 0;

  const status = (document.getElementById('status-filter') as HTMLSelectElement)?.value;
  const timeControl = (document.getElementById('time-control-filter') as HTMLSelectElement)?.value;
  const format = (document.getElementById('format-filter') as HTMLSelectElement)?.value;
  const dateFrom = (document.getElementById('date-from-filter') as HTMLInputElement)?.value;
  const dateTo = (document.getElementById('date-to-filter') as HTMLInputElement)?.value;
  const ratedOnly = (document.getElementById('rated-only-filter') as HTMLInputElement)?.checked;

  if (status) count++;
  if (timeControl) count++;
  if (format) count++;
  if (dateFrom) count++;
  if (dateTo) count++;
  if (ratedOnly) count++;

  if (count > 0) {
    badge.textContent = count.toString();
    badge.style.display = 'flex';
  } else {
    badge.style.display = 'none';
  }
}

/**
 * Set up search and filter event handlers
 */
function setupSearchHandlers(): void {
  const searchBtn = document.getElementById('search-btn');
  const locationInput = document.getElementById('location-search');
  const statusFilter = document.getElementById('status-filter');
  const timeControlFilter = document.getElementById('time-control-filter');
  const formatFilter = document.getElementById('format-filter');
  const dateFromFilter = document.getElementById('date-from-filter');
  const dateToFilter = document.getElementById('date-to-filter');
  const ratedOnlyFilter = document.getElementById('rated-only-filter');
  const sortSelect = document.getElementById('sort-select') as HTMLSelectElement;

  // Search button
  if (searchBtn) {
    searchBtn.addEventListener('click', () => searchTournaments());
  }

  // Enter key in location search
  if (locationInput) {
    locationInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        searchTournaments();
      }
    });
  }

  // Auto-search on filter change
  [statusFilter, timeControlFilter, formatFilter, dateFromFilter, dateToFilter, ratedOnlyFilter].forEach(el => {
    if (el) {
      el.addEventListener('change', () => searchTournaments());
    }
  });

  // Sort change handler
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      currentSortOrder = sortSelect.value;
      applyCurrentSort();
    });
  }
}

/**
 * Sort tournaments based on current sort order
 */
function sortTournaments(tournaments: any[]): any[] {
  const sorted = [...tournaments];
  const [field, direction] = currentSortOrder.split('-');
  const multiplier = direction === 'desc' ? -1 : 1;

  sorted.sort((a, b) => {
    if (field === 'startDate') {
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      return (dateA - dateB) * multiplier;
    } else if (field === 'name') {
      return a.name.localeCompare(b.name) * multiplier;
    }
    return 0;
  });

  return sorted;
}

/**
 * Apply current sort to stored tournaments and re-render
 */
function applyCurrentSort(): void {
  if (currentTournaments.length === 0) return;

  const sorted = sortTournaments(currentTournaments);
  currentTournaments = sorted;

  if (currentViewMode === 'cards') {
    displayTournaments(sorted);
  } else {
    displayTournamentsTable(sorted);
  }
}

// Store last searched location coordinates
let searchedLocationCoords: { lat: number; lon: number } | null = null;

/**
 * Set up location autocomplete
 */
function setupLocationAutocomplete(): void {
  const locationInput = document.getElementById('location-search') as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById('location-search-suggestions') as HTMLElement | null;

  if (!locationInput || !suggestionsDiv) return;

  let debounceTimer: number | undefined;
  let lastResults: any[] = [];

  const searchLocation = async (query: string) => {
    if (query.length < 2) {
      suggestionsDiv.style.display = 'none';
      lastResults = [];
      return;
    }

    try {
      const response = await fetch(`/api/tournaments/location-search?q=${encodeURIComponent(query)}&limit=5`);
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        lastResults = data.results;
        suggestionsDiv.innerHTML = data.results.map((result: any, index: number) => `
          <div class="location-suggestion-item" data-location="${escapeHtml(result.displayName)}" data-index="${index}">
            ${escapeHtml(result.shortDisplayName || result.displayName)}
          </div>
        `).join('');
        suggestionsDiv.style.display = 'block';

        // Add click handlers to suggestions
        suggestionsDiv.querySelectorAll('.location-suggestion-item').forEach(item => {
          item.addEventListener('click', () => {
            const location = (item as HTMLElement).getAttribute('data-location');
            const index = parseInt((item as HTMLElement).getAttribute('data-index') || '0');
            if (location && lastResults[index]) {
              locationInput.value = location;
              suggestionsDiv.style.display = 'none';
              // Use coordinates from the result directly
              const result = lastResults[index];
              if (result.latitude && result.longitude) {
                searchedLocationCoords = {
                  lat: result.latitude,
                  lon: result.longitude
                };
                updateMapToSearchedLocation();
              }
            }
          });
        });
      } else {
        suggestionsDiv.style.display = 'none';
        lastResults = [];
      }
    } catch (error) {
      console.error('Location search error:', error);
      suggestionsDiv.style.display = 'none';
      lastResults = [];
    }
  };

  locationInput.addEventListener('input', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    // Clear searched location when input changes
    searchedLocationCoords = null;
    debounceTimer = window.setTimeout(() => {
      searchLocation(locationInput.value);
    }, 300);
  });

  // Hide suggestions when clicking outside
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as Node;
    if (!locationInput.contains(target) && !suggestionsDiv.contains(target)) {
      suggestionsDiv.style.display = 'none';
    }
  });
}

/**
 * Geocode a location string and update the map
 */
async function geocodeAndUpdateMap(location: string): Promise<void> {
  if (!location) return;

  try {
    const response = await fetch(`/api/tournaments/location-search?q=${encodeURIComponent(location)}&limit=1`);
    const data = await response.json();

    if (data.results && data.results.length > 0 && data.results[0].latitude) {
      searchedLocationCoords = {
        lat: data.results[0].latitude,
        lon: data.results[0].longitude
      };
      updateMapToSearchedLocation();
    }
  } catch (error) {
    console.error('Geocode error:', error);
  }
}

/**
 * Update map to center on searched location with radius
 */
function updateMapToSearchedLocation(): void {
  if (!directoryMap || !searchedLocationCoords) return;

  const radiusSelect = document.getElementById('radius-select') as HTMLSelectElement;
  const radiusKm = radiusSelect ? parseInt(radiusSelect.value) : 50;
  const radiusMeters = radiusKm * 1000;

  // Hide user location prompt since we have a location
  hideLocationPrompt();

  // Update or create the search location marker (reuse user marker style but different)
  if (userLocationMarker) {
    userLocationMarker.setLatLng([searchedLocationCoords.lat, searchedLocationCoords.lon]);
  } else {
    const searchIcon = L.divIcon({
      className: 'user-location-marker-icon',
      html: '<div class="user-location-marker"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7]
    });
    userLocationMarker = L.marker([searchedLocationCoords.lat, searchedLocationCoords.lon], { icon: searchIcon })
      .addTo(directoryMap);
  }

  // Update radius circle
  if (radiusCircle) {
    directoryMap.removeLayer(radiusCircle);
  }
  radiusCircle = L.circle([searchedLocationCoords.lat, searchedLocationCoords.lon], {
    radius: radiusMeters,
    className: 'radius-circle',
    fillColor: '#16a085',
    fillOpacity: 0.1,
    color: '#16a085',
    weight: 2,
    opacity: 0.4
  }).addTo(directoryMap);

  // Fit map to show the circle
  directoryMap.fitBounds(radiusCircle.getBounds(), { padding: [20, 20] });
}

/**
 * Search tournaments with current filters
 */
async function searchTournaments(): Promise<void> {
  const loadingEl = document.getElementById('tournaments-loading');
  const listEl = document.getElementById('tournaments-list');
  const tableEl = document.getElementById('tournaments-table');
  const emptyEl = document.getElementById('tournaments-empty');
  const countEl = document.getElementById('tournaments-count');

  // Show loading
  if (loadingEl) loadingEl.style.display = 'block';
  if (listEl) listEl.innerHTML = '';
  if (tableEl) tableEl.innerHTML = '';
  if (emptyEl) emptyEl.style.display = 'none';

  try {
    // Build filters
    const filters = buildFilters();

    // Geocode location and update map if we have a location but no coordinates
    const locationInput = document.getElementById('location-search') as HTMLInputElement;
    if (locationInput?.value && !searchedLocationCoords && !userCoords) {
      await geocodeAndUpdateMap(locationInput.value);
    }

    // Fetch tournaments
    const result = await searchPublicTournaments(filters);

    // Sort and store tournaments
    currentTournaments = sortTournaments(result.tournaments);

    // Hide loading
    if (loadingEl) loadingEl.style.display = 'none';

    // Show count
    if (countEl) {
      countEl.textContent = window.i18n.t('tournaments.card.tournamentsFound', { count: result.count });
    }

    // Show results
    if (currentTournaments.length === 0) {
      if (emptyEl) emptyEl.style.display = 'block';
    } else {
      // Display based on current view mode
      if (currentViewMode === 'cards') {
        displayTournaments(currentTournaments);
      } else {
        displayTournamentsTable(currentTournaments);
      }

      // Update map markers
      updateTournamentMarkers(result.tournaments);
    }
  } catch (error) {
    console.error('Failed to search tournaments:', error);
    if (loadingEl) loadingEl.style.display = 'none';
    showError(window.i18n.t('tournaments.card.searchError'));
  }
}

/**
 * Build search filters from form inputs
 */
function buildFilters(): PublicTournamentFilters {
  const filters: PublicTournamentFilters = {};

  const location = (document.getElementById('location-search') as HTMLInputElement)?.value;
  const radius = (document.getElementById('radius-select') as HTMLSelectElement)?.value;
  const status = (document.getElementById('status-filter') as HTMLSelectElement)?.value;
  const timeControl = (document.getElementById('time-control-filter') as HTMLSelectElement)?.value;
  const format = (document.getElementById('format-filter') as HTMLSelectElement)?.value;
  const dateFrom = (document.getElementById('date-from-filter') as HTMLInputElement)?.value;
  const dateTo = (document.getElementById('date-to-filter') as HTMLInputElement)?.value;
  const ratedOnly = (document.getElementById('rated-only-filter') as HTMLInputElement)?.checked;

  if (location) filters.location = location;
  if (radius) filters.radius = parseInt(radius);
  if (status) filters.status = status;
  if (timeControl) filters.timeControl = timeControl;
  if (format) filters.format = format;
  if (dateFrom) filters.startDate = dateFrom;
  if (dateTo) filters.endDate = dateTo;
  if (ratedOnly) filters.isRated = true;

  return filters;
}

/**
 * Get registration status badge/button HTML
 */
function getRegistrationActionHtml(tournament: any, isSmall: boolean = false): string {
  const btnClass = isSmall ? 'btn btn-secondary btn-small' : 'btn btn-secondary';

  // If user has a registration, show status badge
  if (tournament.userRegistration) {
    const status = tournament.userRegistration.status;
    const statusLabels: Record<string, string> = {
      'pending': window.i18n.t('registration.status.pending', 'Pending'),
      'accepted': window.i18n.t('registration.status.accepted', 'Accepted'),
      'rejected': window.i18n.t('registration.status.rejected', 'Rejected'),
      'waitlist': window.i18n.t('registration.status.waitlist', 'Waitlist'),
      'withdrawn': window.i18n.t('registration.status.withdrawn', 'Withdrawn')
    };
    const statusClasses: Record<string, string> = {
      'pending': 'badge-status-pending',
      'accepted': 'badge-status-accepted',
      'rejected': 'badge-status-rejected',
      'waitlist': 'badge-status-waitlist',
      'withdrawn': 'badge-status-withdrawn'
    };
    const statusIcons: Record<string, string> = {
      'pending': '⏳',
      'accepted': '✓',
      'rejected': '✗',
      'waitlist': '📋',
      'withdrawn': '↩'
    };
    const label = statusLabels[status] || status;
    const cssClass = statusClasses[status] || 'badge-status-pending';
    const icon = statusIcons[status] || '';

    return `<span class="badge registration-status ${cssClass}">${icon} ${label}</span>`;
  }

  // If registration is allowed, show register button
  if (tournament.allowSelfRegistration) {
    const hasStripe = !!tournament.stripeConnectedAccountId;
    const allowOnsitePayment = !!tournament.allowOnsitePayment;
    // Safely encode entry fees to avoid breaking onclick handler
    const encodedFees = encodeURIComponent(JSON.stringify(tournament.entryFees || []));
    return `<button class="${btnClass}" onclick="openRegistrationModal('${tournament.id}', '${escapeHtml(tournament.name).replace(/'/g, "\\'")}', JSON.parse(decodeURIComponent('${encodedFees}')), '${tournament.timeControl || 'classical'}', ${hasStripe}, ${allowOnsitePayment})">${window.i18n.t('tournaments.card.register', 'Register')}</button>`;
  }

  return '';
}

/**
 * Display tournaments in the grid
 */
function displayTournaments(tournaments: any[]): void {
  const listEl = document.getElementById('tournaments-list');
  if (!listEl) return;

  listEl.innerHTML = tournaments.map(tournament => {
    const tournamentUrl = tournament.shareToken ? `/share/${tournament.shareToken}` : `/?tournamentId=${tournament.id}`;
    return `
    <div class="tournament-card">
      <a href="${tournamentUrl}" class="tournament-card-logo">
        <img src="${escapeHtml(tournament.logo || DEFAULT_TOURNAMENT_LOGO)}" alt="${escapeHtml(tournament.name)} logo" onerror="this.src='${DEFAULT_TOURNAMENT_LOGO}'">
      </a>

      <div class="tournament-card-header">
        <h3>${escapeHtml(tournament.name)}</h3>
        ${tournament.isRated ? `<span class="badge badge-rated">${window.i18n.t('tournaments.card.rated')}</span>` : ''}
      </div>

      <div class="tournament-card-body">
        <p class="tournament-info">
          <strong>${window.i18n.t('tournaments.card.format')}</strong> ${formatTournamentFormat(tournament.format)}<br>
          ${tournament.timeControl ? `<strong>${window.i18n.t('tournaments.card.timeControl')}</strong> ${escapeHtml(tournament.timeControl)}<br>` : ''}
          <strong>${window.i18n.t('tournaments.card.date')}</strong> ${formatDate(tournament.startDate)}
          ${tournament.endDate && tournament.endDate !== tournament.startDate ? ` - ${formatDate(tournament.endDate)}` : ''}<br>
          ${tournament.location ? `<strong>${window.i18n.t('tournaments.card.location')}</strong> ${escapeHtml(simplifyVenueName(tournament.location))}<br>` : ''}
          ${tournament.numberOfRounds ? `<strong>${window.i18n.t('tournaments.card.rounds')}</strong> ${tournament.numberOfRounds}<br>` : ''}
          <strong>${window.i18n.t('tournaments.card.entryFee')}</strong> ${formatEntryFees(tournament.entryFees)}<br>
        </p>
      </div>

      <div class="tournament-card-actions">
        <a href="${tournamentUrl}" class="btn btn-primary">${window.i18n.t('tournaments.card.viewDetails')}</a>
        ${getRegistrationActionHtml(tournament)}
      </div>
    </div>
  `;
  }).join('');
}

/**
 * Display tournaments in table view
 */
function displayTournamentsTable(tournaments: any[]): void {
  const tableEl = document.getElementById('tournaments-table');
  if (!tableEl) return;

  const getStatusClass = (tournament: any): string => {
    const now = new Date();
    const start = new Date(tournament.startDate);
    const end = tournament.endDate ? new Date(tournament.endDate) : start;

    if (now < start) return 'upcoming';
    if (now > end) return 'completed';
    return 'ongoing';
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'upcoming': window.i18n.t('status.upcoming', 'Upcoming'),
      'ongoing': window.i18n.t('status.ongoing', 'Ongoing'),
      'completed': window.i18n.t('status.completed', 'Completed')
    };
    return labels[status] || status;
  };

  const rows = tournaments.map(tournament => {
    const status = getStatusClass(tournament);
    const tournamentUrl = tournament.shareToken ? `/share/${tournament.shareToken}` : `/?tournamentId=${tournament.id}`;

    return `
      <tr>
        <td class="tournament-table-name">
          <a href="${tournamentUrl}">${escapeHtml(tournament.name)}</a>
          ${tournament.isRated ? '<span class="badge badge-rated" style="margin-left: 6px;">Rated</span>' : ''}
        </td>
        <td class="tournament-table-location" title="${escapeHtml(tournament.location || '')}">${escapeHtml(simplifyVenueName(tournament.location) || '-')}</td>
        <td class="tournament-table-date">${formatDate(tournament.startDate)}${tournament.endDate && tournament.endDate !== tournament.startDate ? ' - ' + formatDate(tournament.endDate) : ''}</td>
        <td class="tournament-table-format">${formatTournamentFormat(tournament.format)}</td>
        <td><span class="badge badge-status ${status}">${getStatusLabel(status)}</span></td>
        <td class="tournament-table-actions">
          <a href="${tournamentUrl}" class="btn btn-primary btn-small">${window.i18n.t('tournaments.card.viewDetails', 'View')}</a>
          ${getRegistrationActionHtml(tournament, true)}
        </td>
      </tr>
    `;
  }).join('');

  tableEl.innerHTML = `
    <table class="tournaments-table">
      <thead>
        <tr>
          <th>${window.i18n.t('tournaments.table.name', 'Tournament')}</th>
          <th>${window.i18n.t('tournaments.table.location', 'Location')}</th>
          <th>${window.i18n.t('tournaments.table.date', 'Date')}</th>
          <th>${window.i18n.t('tournaments.table.format', 'Format')}</th>
          <th>${window.i18n.t('tournaments.table.status', 'Status')}</th>
          <th>${window.i18n.t('tournaments.table.actions', 'Actions')}</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
}

/**
 * Format tournament format for display
 */
function formatTournamentFormat(format: string): string {
  const formatKeys: Record<string, string> = {
    'swiss': 'tournament.format.swiss',
    'round-robin': 'tournament.format.roundRobin',
    'knockout': 'tournament.format.knockout'
  };
  const key = formatKeys[format];
  return key ? window.i18n.t(key) : format;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format entry fees for display
 */
function formatEntryFees(entryFees: any[]): string {
  if (!entryFees || entryFees.length === 0) {
    return window.i18n.t('tournaments.card.free');
  }

  // Format amounts with proper currency formatting (amount is in cents)
  const fees = entryFees.map(fee => {
    const amount = (fee.amount || 0) / 100; // Convert cents to dollars/euros
    const currency = (fee.currency || 'EUR').toUpperCase();
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  });

  return fees.join(', ');
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Simplify venue name by removing multi-language country variants
 */
function simplifyVenueName(venue: string | null | undefined): string {
  if (!venue) return '';

  return venue.split(',')
    .map(part => {
      const trimmed = part.trim();
      return trimmed.includes('/') ? trimmed.split('/')[0].trim() : trimmed;
    })
    .join(', ');
}

/**
 * Show registration success banner
 */
function showRegistrationBanner(): void {
  const banner = document.createElement('div');
  banner.className = 'registration-success-banner';
  const message = window.i18n ? window.i18n.t('registration.submitted', 'Registration submitted! Awaiting organizer approval.') : 'Registration submitted! Awaiting organizer approval.';
  banner.innerHTML = `<span>✓ ${message}</span>`;
  banner.style.cssText = `
    position: fixed;
    top: 70px;
    left: 50%;
    transform: translateX(-50%);
    background: #28a745;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 1000;
    font-weight: 500;
    animation: slideDown 0.3s ease-out;
  `;

  // Add animation styles
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from { transform: translateX(-50%) translateY(-20px); opacity: 0; }
      to { transform: translateX(-50%) translateY(0); opacity: 1; }
    }
    @keyframes slideUp {
      from { transform: translateX(-50%) translateY(0); opacity: 1; }
      to { transform: translateX(-50%) translateY(-20px); opacity: 0; }
    }
  `;
  document.head.appendChild(style);

  document.body.appendChild(banner);

  // Auto-remove after 4 seconds
  setTimeout(() => {
    banner.style.animation = 'slideUp 0.3s ease-out forwards';
    setTimeout(() => banner.remove(), 300);
  }, 4000);
}

/**
 * Show error message
 */
function showError(message: string): void {
  if ((window as any).showNotification) {
    (window as any).showNotification(message, 'error');
  } else {
    alert(message);
  }
}

// Initialize after i18n is ready to avoid race condition with translations
function initWhenReady(): void {
  // Check if i18n is already initialized
  if (document.body.classList.contains('i18n-ready')) {
    initTournamentsDirectory();
  } else {
    // Wait for i18n to be ready
    window.addEventListener('i18nReady', () => initTournamentsDirectory(), { once: true });
  }
}

// Initialize on DOM load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhenReady);
} else {
  initWhenReady();
}
